import React from "react";
import { MediaItem } from "../../../../types/MediaItem";
import {
  ChatStreamError,
} from "../../../../services/chatService";
import {
  GeneratedPostsData,
  Message,
  MessageActions,
  UIAction,
  ImageGenerationData,
  DraftPost,
  BlogPost,
  OutreachEmailDraft,
} from "../types";
import { ConnectedProfile } from "../../../../types/customer";
import { NewChatHeader } from "./NewChatHeader";
import { NewChatWelcomeView } from "./NewChatWelcomeView";
import type { AgentProposalStarter } from "./NewChatWelcomeView";
import { NewChatThreadView } from "./NewChatThreadView";
import { RightPanelHost } from "./RightPanelHost";
import { VideoDockToggle, type VideoDockView } from "./VideoDockToggle";
import { UGCVideoRightPanel } from "./UGCVideoRightPanel";
import { SocialPostsRightPanel } from "./SocialPostsRightPanel";
import { ScheduleModal } from "./ScheduleModal";
import { MediaLibraryModal } from "./MediaLibraryModal";
import { FeatureUpgradeModal } from "../../../../features/feature-access";
import type { FeatureKey } from "../../../../features/feature-access";
import { DEFAULT_BASE_VIDEO_DURATION_SEC } from "../../../../features/ugc-video/constants";
import type { SingleCardBusy } from "../hooks/usePostActions";

interface NewChatWindowLayoutProps {
  chatAnimationsCss: string;
  surfaceBg: string;
  headerBar: string;
  splitResize: string;
  rightPanelMode: "video" | "social_posts" | "wordpress_blog" | null;
  /** Bumped per new video session; used as the Video Creator's React key to force a clean remount. */
  videoSessionKey?: number;
  canvasRequestSignal?: number;
  ugcVideoDockView: VideoDockView;
  onUgcVideoDockViewChange: (view: VideoDockView) => void;
  setRightPanelMode: (mode: "video" | "social_posts" | "wordpress_blog" | null) => void;
  splitContainerRef: React.RefObject<HTMLDivElement>;
  chatWidthPercent: number;
  setIsResizing: (isResizing: boolean) => void;
  agentName: string;
  /** Shows the AI visibility check-up toggle in the header for the Visibility Manager (SGL-507). */
  agentType?: string;
  agentAvatar?: string;
  agentId?: string;
  customerId?: string;
  /** When set, UGC AI image tab requires `generate_image` in this list (matches agent tools from API). */
  agentTools?: string[];
  availableAgents: Array<{ id: string; name: string; avatar?: string }>;
  showAgentDropdown: boolean;
  embedded: boolean;
  healthError: string | null;
  isChatHealthy: boolean | null;
  onAgentChange?: (agentId: string) => void;
  onEditAgent: () => void;
  onBack?: () => void;
  showNewChatWelcome: boolean;
  welcomeIsExiting: boolean;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  starterActionsForDisplay: UIAction[];
  isLoading: boolean;
  onActionClick: (action: UIAction) => void;
  starterSuggestionAccentPill: string;
  starterSuggestionNeutralPill: string;
  showConversationStarters: boolean;
  conversationStarters: Array<{ id: string; suggestion: string }>;
  onConversationStarter: (suggestion: string) => void;
  error: ChatStreamError | string | null;
  onDismissError: () => void;
  chatInputSharedProps: any;
  showSaveToPlatform: boolean;
  videoForPlatform: any;
  onCloseSavePanel: () => void;
  idToken: string;
  saveToPlatformInitialPlatforms: any[];
  isLoadingThreadHistory: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  lastUserMessageRef: React.MutableRefObject<HTMLDivElement | null>;
  lastUserMessageIndex: number;
  messages: Message[];
  userName?: string;
  onPublish: any;
  onSchedule: any;
  onSavePosts: any;
  onSaveSingleDraft: (post: DraftPost) => void | Promise<void>;
  onPublishSingleDraft: (post: DraftPost) => void | Promise<void>;
  onScheduleSingleDraft: (post: DraftPost) => void | Promise<void>;
  singleCardBusy: SingleCardBusy | null;
  onSaveVideos: (videos: any[]) => Promise<void>;
  isPublishing: boolean;
  isScheduling: boolean;
  isSavingPosts: boolean;
  isSavingVideos: boolean;
  currentStatus: "thinking" | "tool_selected" | "tool_executing" | "generating" | null;
  currentToolName: string | null;
  toolSteps: import("../hooks/useChatStreaming").ToolStep[];
  streamingContent: string;
  streamingThinkingContent?: string;
  streamingActions: MessageActions | null;
  streamingUiActions: UIAction[] | null;
  /** Perplexity citation URLs, updated progressively during streaming (SGL-101). */
  streamingCitations: string[] | null;
  streamingGeneratedImages: ImageGenerationData | null;
  /** True once the current generate_image_batch call's images have arrived — distinct
   *  from streamingGeneratedImages, see useChatStreaming.ts. */
  bulkImagesReady: boolean;
  streamingGeneratedPosts: GeneratedPostsData | null;
  requestInsertImageForPost: (
    tempId: string,
    mediaType?: "image" | "video"
  ) => void;
  /** Meta carousel: open My Library multi-select for this post temp id.
   *  Pass the post's platform so FB scopes the picker to images only. */
  requestInsertCarouselForPost?: (tempId: string, platform?: string) => void;
  /** LinkedIn document post (SGL-174): open My Library filtered to PDFs. */
  requestInsertDocumentForPost?: (tempId: string) => void;
  /** Open OS file picker for image/video on a generated post (right panel). */
  openLocalFileForPost: (tempId: string) => void;
  /** Open the right-panel canvas for a specific post. */
  onOpenPostCanvas: (post: DraftPost, allPosts: GeneratedPostsData) => void;
  /** Open the right-panel blog editor (canvas) for a message's blog post. */
  onOpenBlogCanvas?: (blog: BlogPost) => void;
  /** Applies an edit (subject/body) or Approve to a message's outreach email draft. */
  onOutreachEmailChange?: (message: Message, next: OutreachEmailDraft) => void;
  /** Sends a new chat message asking the model to redraft the outreach email. */
  onOutreachEmailRegenerate?: () => void;
  /** Persists an inline edit on a message's contact batch (SGL-81, rendered inline in chat — no panel). */
  onPatchContact?: (message: Message, index: number, fields: import("../../../../services/contactImportService").PatchableContactFields) => Promise<void>;
  onWriteCardIntros?: (message: Message, contacts: import("../types").ScannedCardDetails[], brief: string, captureContext: string) => Promise<void>;
  /** A correction on a scan, recorded before the owner asks the agent anything. */
  onCardDetailsEdited?: (message: Message, contacts: import("../types").ScannedCardDetails[], captureContext: string) => void;
  /** SGL-328: what the Business Profile card can ask the chat window to do. */
  gbpActions?: import("../types").GbpMessageActions;
  /** Upserts the selected contacts from a message's batch into GHL (SGL-81). */
  onImportContactsFromMessage?: (message: Message, selectedIndices: number[], tags: string[]) => Promise<void>;
  isImportingContacts?: boolean;
  contactUpsertError?: string | null;
  /** Fetch the next page for a message's contacts list (SGL-81 "ask my contacts"). */
  onLoadMoreContacts?: (message: Message) => Promise<void>;
  /** Runs a filtered search, replacing a message's contacts list (SGL-81). */
  onSearchContacts?: (message: Message, query: string) => Promise<void>;
  /** The post temp_id to focus/scroll to in the right panel. */
  panelFocusTempId?: string | null;
  ugcVideo: any;
  videoGen: any;
  onCloseVideoPanel: () => void;
  onGenerateScript: (prompt: string) => Promise<void>;
  scriptContent: string;
  setScriptContent: React.Dispatch<React.SetStateAction<string>>;
  streamingScriptContent: string;
  isGeneratingScript: boolean;
  onSaveToPlatform: (
    videoUrl: string,
    selectedPlatforms: any[],
    script?: string,
    generatedCaptionText?: string,
    tags?: string[],
    linkedinTarget?: "personal" | "business"
  ) => Promise<void>;
  generatedCaption: string;
  setGeneratedCaption: React.Dispatch<React.SetStateAction<string>>;
  onGenerateCaption: (script: string) => Promise<void>;
  isGeneratingCaption: boolean;
  onLoadDraftSelections?: (data: {
    selectedAvatar: any;
    selectedVoice: any;
    timelineSegments: any[];
    scriptContent: string;
    generatedCaption: string;
    generatedVideoId: string | null;
    generatedVideoUrl: string | null;
    baseVideoDurationSeconds: number | null;
    videoStatus: string | null;
  }) => void;
  panelSocialPosts: any;
  onCloseSocialPostsPanel: () => void;
  /** Fully-built blog editor panel node, rendered when rightPanelMode === "wordpress_blog". */
  wordpressBlogPanel?: React.ReactNode;
  showScheduleModal: boolean;
  onCloseScheduleModal: () => void;
  scheduleDateTime: string;
  onDateTimeChange: (value: string) => void;
  scheduleTimezone: string;
  onTimezoneChange: (tz: string) => void;
  selectedPostData: any;
  onScheduleSubmit: () => Promise<void>;
  showMediaLibrary: boolean;
  onCloseMediaLibrary: () => void;
  mediaLibraryItems: MediaItem[];
  selectedImages: MediaItem[];
  onImageSelect: (image: MediaItem) => void;
  loadingMedia: boolean;
  mediaLibraryMultiSelect?: boolean;
  onMediaLibraryDone?: () => void;
  mediaLibraryMaxSlots?: number;
  /** Which Pro feature the user just bumped into, or null when none. */
  upgradeFeature: FeatureKey | null;
  onCloseUpgradeMessage: () => void;
  hasConnectedAccounts: boolean;
  connectedProfiles: ConnectedProfile[];
  welcomeVariant?: "default" | "communication";
  chipSuggestionsCard?: React.ReactNode;
  /** Forwarded to NewChatWelcomeView: an agent's own starter card. */
  agentProposal?: AgentProposalStarter;
}

export const NewChatWindowLayout: React.FC<NewChatWindowLayoutProps> = ({
  chatAnimationsCss,
  surfaceBg,
  headerBar,
  splitResize,
  rightPanelMode,
  videoSessionKey,
  canvasRequestSignal,
  ugcVideoDockView,
  onUgcVideoDockViewChange,
  setRightPanelMode,
  splitContainerRef,
  chatWidthPercent,
  setIsResizing,
  agentName,
  agentType,
  agentAvatar,
  agentId,
  customerId,
  agentTools,
  availableAgents,
  showAgentDropdown,
  embedded,
  healthError,
  isChatHealthy,
  onAgentChange,
  onEditAgent,
  onBack,
  showNewChatWelcome,
  welcomeIsExiting,
  messagesContainerRef,
  starterActionsForDisplay,
  isLoading,
  onActionClick,
  starterSuggestionAccentPill,
  starterSuggestionNeutralPill,
  showConversationStarters,
  conversationStarters,
  onConversationStarter,
  error,
  onDismissError,
  chatInputSharedProps,
  showSaveToPlatform,
  videoForPlatform,
  onCloseSavePanel,
  idToken,
  saveToPlatformInitialPlatforms,
  isLoadingThreadHistory,
  messagesEndRef,
  lastUserMessageRef,
  lastUserMessageIndex,
  messages,
  userName,
  onPublish,
  onSchedule,
  onSavePosts,
  onSaveSingleDraft,
  onPublishSingleDraft,
  onScheduleSingleDraft,
  singleCardBusy,
  onSaveVideos,
  isPublishing,
  isScheduling,
  isSavingPosts,
  isSavingVideos,
  currentStatus,
  currentToolName,
  toolSteps,
  streamingContent,
  streamingThinkingContent,
  streamingActions,
  streamingUiActions,
  streamingCitations,
  streamingGeneratedImages,
  bulkImagesReady,
  streamingGeneratedPosts,
  requestInsertImageForPost,
  requestInsertCarouselForPost,
  requestInsertDocumentForPost,
  openLocalFileForPost,
  onOpenPostCanvas,
  onOpenBlogCanvas,
  onOutreachEmailChange,
  onOutreachEmailRegenerate,
  onPatchContact,
  onWriteCardIntros,
  onCardDetailsEdited,
  gbpActions,
  onImportContactsFromMessage,
  isImportingContacts,
  contactUpsertError,
  onLoadMoreContacts,
  onSearchContacts,
  panelFocusTempId,
  ugcVideo,
  videoGen,
  onCloseVideoPanel,
  onGenerateScript,
  scriptContent,
  setScriptContent,
  streamingScriptContent,
  isGeneratingScript,
  onSaveToPlatform,
  generatedCaption,
  setGeneratedCaption,
  onGenerateCaption,
  isGeneratingCaption,
  onLoadDraftSelections,
  panelSocialPosts,
  onCloseSocialPostsPanel,
  wordpressBlogPanel,
  showScheduleModal,
  onCloseScheduleModal,
  scheduleDateTime,
  onDateTimeChange,
  scheduleTimezone,
  onTimezoneChange,
  selectedPostData,
  onScheduleSubmit,
  showMediaLibrary,
  onCloseMediaLibrary,
  mediaLibraryItems,
  selectedImages,
  onImageSelect,
  loadingMedia,
  mediaLibraryMultiSelect,
  onMediaLibraryDone,
  mediaLibraryMaxSlots,
  upgradeFeature,
  onCloseUpgradeMessage,
  hasConnectedAccounts,
  connectedProfiles,
  welcomeVariant = "default",
  chipSuggestionsCard,
  agentProposal,
}) => {
  const videoPanel = (
    <UGCVideoRightPanel
      key={videoSessionKey}
      variant="panel"
      isOpen={true}
      onClose={onCloseVideoPanel}
      panelHeaderAccessory={
        <VideoDockToggle view={ugcVideoDockView} onChange={onUgcVideoDockViewChange} />
      }
      avatars={ugcVideo.avatars}
      customAvatars={ugcVideo.customAvatars}
      onRefreshAvatars={ugcVideo.fetchData}
      onReloadAvatars={ugcVideo.fetchAvatars}
      onReloadVoices={ugcVideo.fetchVoices}
      voices={ugcVideo.voices}
      selectedAvatar={ugcVideo.selectedAvatar}
      selectedVoice={ugcVideo.selectedVoice}
      selectedMediaIds={ugcVideo.selectedMediaIds}
      timelineSegments={ugcVideo.timelineSegments}
      loadingAvatars={ugcVideo.loadingAvatars}
      avatarsFetched={ugcVideo.avatarsFetched}
      loadingVoices={ugcVideo.loadingVoices}
      onSelectAvatar={ugcVideo.selectAvatar}
      onSelectVoice={ugcVideo.selectVoice}
      onToggleMedia={ugcVideo.toggleMediaSelection}
      onClearSelectedMedia={() => ugcVideo.setTimelineSegments([])}
      onReorderSegments={ugcVideo.reorderSegments}
      onUpdateSegment={ugcVideo.updateSegment}
      onRemoveSegment={ugcVideo.removeSegment}
      onPlaceMediaOnTimeline={(mediaId, timeSec) =>
        ugcVideo.placeMediaOnTimelineAt(
          mediaId,
          timeSec,
          videoGen.baseVideoDurationSeconds ?? DEFAULT_BASE_VIDEO_DURATION_SEC
        )
      }
      onClearSelections={ugcVideo.clearSelections}
      customerId={customerId || null}
      idToken={idToken}
      agentId={agentId || ""}
      agentTools={agentTools}
      onGenerateScript={onGenerateScript}
      scriptContent={scriptContent}
      setScriptContent={setScriptContent}
      streamingScriptContent={streamingScriptContent}
      isGeneratingScript={isGeneratingScript}
      error={ugcVideo.error ?? (typeof error === 'object' && error !== null && (error as any).type === 'INSUFFICIENT_CREDITS' ? error : null)}
      isGeneratingVideo={videoGen.isGenerating}
      generatedVideoUrl={videoGen.generatedVideoUrl}
      videoError={videoGen.videoError}
      generationMessage={videoGen.generationMessage}
      videoStatus={videoGen.videoStatus}
      elapsedSeconds={videoGen.elapsedSeconds}
      baseVideoDurationSeconds={videoGen.baseVideoDurationSeconds}
      generatedVideoId={videoGen.generatedVideoId}
      onAssemblyCompleteVideoUrl={videoGen.updateVideoUrl}
      onGenerateVideo={videoGen.generateVideo}
      onRetryCheck={videoGen.retryCheck}
      onResetVideo={videoGen.reset}
      isSavingVideos={isSavingVideos}
      onSaveToPlatform={onSaveToPlatform}
      connectedProfiles={connectedProfiles}
      generatedCaption={generatedCaption}
      setGeneratedCaption={setGeneratedCaption}
      onGenerateCaption={onGenerateCaption}
      isGeneratingCaption={isGeneratingCaption}
      onLoadDraftSelections={onLoadDraftSelections}
    />
  );

  const chatContent = (
    <>
      <NewChatHeader
        agentName={agentName}
        agentType={agentType}
        agentAvatar={agentAvatar}
        agentId={agentId}
        customerId={customerId}
        idToken={idToken}
        availableAgents={availableAgents}
        showAgentDropdown={showAgentDropdown}
        embedded={embedded}
        headerBar={headerBar}
        healthError={healthError}
        isChatHealthy={isChatHealthy}
        isLoading={isLoading}
        onAgentChange={onAgentChange}
        onEditAgent={onEditAgent}
        onBack={onBack}
      />

      {showNewChatWelcome ? (
        <NewChatWelcomeView
          surfaceBg={surfaceBg}
          messagesContainerRef={messagesContainerRef}
          starterActionsForDisplay={starterActionsForDisplay}
          isLoading={isLoading}
          onActionClick={(action) => onActionClick(action)}
          starterSuggestionAccentPill={starterSuggestionAccentPill}
          starterSuggestionNeutralPill={starterSuggestionNeutralPill}
          showConversationStarters={showConversationStarters}
          conversationStarters={conversationStarters}
          onConversationStarter={onConversationStarter}
          error={error}
          onDismissError={onDismissError}
          chatInputSharedProps={chatInputSharedProps}
          showSaveToPlatform={showSaveToPlatform}
          videoForPlatform={videoForPlatform}
          onCloseSavePanel={onCloseSavePanel}
          customerId={customerId}
          idToken={idToken}
          agentId={agentId}
          saveToPlatformInitialPlatforms={saveToPlatformInitialPlatforms}
          isExiting={welcomeIsExiting}
          userName={userName}
          welcomeVariant={welcomeVariant}
          chipSuggestionsCard={chipSuggestionsCard}
          agentProposal={agentProposal}
        />
      ) : (
        <NewChatThreadView
          surfaceBg={surfaceBg}
          isLoadingThreadHistory={isLoadingThreadHistory}
          messagesContainerRef={messagesContainerRef}
          messagesEndRef={messagesEndRef}
          lastUserMessageRef={lastUserMessageRef}
          lastUserMessageIndex={lastUserMessageIndex}
          messages={messages}
          onPublish={onPublish}
          onSchedule={onSchedule}
          onSavePosts={onSavePosts}
          onSaveVideos={onSaveVideos}
          onActionClick={onActionClick}
          isPublishing={isPublishing}
          isScheduling={isScheduling}
          isSavingPosts={isSavingPosts}
          isSavingVideos={isSavingVideos}
          isLoading={isLoading}
          currentStatus={currentStatus}
          currentToolName={currentToolName}
          toolSteps={toolSteps}
          streamingContent={streamingContent}
          streamingThinkingContent={streamingThinkingContent}
          streamingActions={streamingActions}
          streamingUiActions={streamingUiActions}
          streamingCitations={streamingCitations}
          streamingGeneratedImages={streamingGeneratedImages}
          bulkImagesReady={bulkImagesReady}
          streamingGeneratedPosts={streamingGeneratedPosts}
          requestInsertImageForPost={requestInsertImageForPost}
          onOpenPostCanvas={onOpenPostCanvas}
          onOpenBlogCanvas={onOpenBlogCanvas}
          onOutreachEmailChange={onOutreachEmailChange}
          onOutreachEmailRegenerate={onOutreachEmailRegenerate}
          onPatchContact={onPatchContact}
          onWriteCardIntros={onWriteCardIntros}
          onCardDetailsEdited={onCardDetailsEdited}
          gbpActions={gbpActions}
          onImportContactsFromMessage={onImportContactsFromMessage}
          isImportingContacts={isImportingContacts}
          contactUpsertError={contactUpsertError}
          onLoadMoreContacts={onLoadMoreContacts}
          onSearchContacts={onSearchContacts}
          error={error}
          onDismissError={onDismissError}
          chatInputSharedProps={chatInputSharedProps}
          chipSuggestionsCard={chipSuggestionsCard}
          showSaveToPlatform={showSaveToPlatform}
          videoForPlatform={videoForPlatform}
          onCloseSavePanel={onCloseSavePanel}
          customerId={customerId}
          idToken={idToken}
          agentId={agentId}
          saveToPlatformInitialPlatforms={saveToPlatformInitialPlatforms}
        />
      )}
    </>
  );

  return (
    <>
      <style>{chatAnimationsCss}</style>
      <div className={`flex h-full min-h-0 w-full font-sans ${surfaceBg}`}>
        <RightPanelHost
          rightPanelMode={rightPanelMode}
          canvasRequestSignal={canvasRequestSignal}
          videoDockView={ugcVideoDockView}
          onVideoDockViewChange={onUgcVideoDockViewChange}
          splitContainerRef={splitContainerRef}
          chatWidthPercent={chatWidthPercent}
          setIsResizing={setIsResizing}
          splitResizeClass={splitResize}
          surfaceBg={surfaceBg}
          chatContent={chatContent}
          videoPanel={videoPanel}
          socialPostsPanel={
            <SocialPostsRightPanel
              surfaceBg={surfaceBg}
              headerBar={headerBar}
              panelSocialPosts={panelSocialPosts}
              isLoading={isLoading}
              onSaveSingleDraft={onSaveSingleDraft}
              onPublishSingleDraft={onPublishSingleDraft}
              onScheduleSingleDraft={onScheduleSingleDraft}
              singleCardBusy={singleCardBusy}
              onInsertImageFromLibrary={requestInsertImageForPost}
              onInsertCarouselFromLibrary={requestInsertCarouselForPost}
              onInsertDocumentFromLibrary={requestInsertDocumentForPost}
              onInsertImageFromLocalFile={openLocalFileForPost}
              onClose={onCloseSocialPostsPanel}
              focusTempId={panelFocusTempId}
              hasConnectedAccounts={hasConnectedAccounts}
              connectedProfiles={connectedProfiles}
            />
          }
          wordpressBlogPanel={wordpressBlogPanel}
        />
      <ScheduleModal
        isOpen={showScheduleModal}
        onClose={onCloseScheduleModal}
        scheduleDateTime={scheduleDateTime}
        onDateTimeChange={onDateTimeChange}
        scheduleTimezone={scheduleTimezone}
        onTimezoneChange={onTimezoneChange}
        selectedPostData={selectedPostData}
        onSchedule={onScheduleSubmit}
        isScheduling={isScheduling}
      />

      <MediaLibraryModal
        isOpen={showMediaLibrary}
        onClose={onCloseMediaLibrary}
        mediaItems={mediaLibraryItems}
        selectedImages={selectedImages}
        onImageSelect={onImageSelect}
        loading={loadingMedia}
        multiSelectMode={mediaLibraryMultiSelect}
        onPressDone={onMediaLibraryDone}
        maxSlots={mediaLibraryMaxSlots}
      />

      {/* One modal for every plan gate — the blocked feature names itself. */}
      {upgradeFeature && (
        <FeatureUpgradeModal
          isOpen
          onClose={onCloseUpgradeMessage}
          featureKey={upgradeFeature}
        />
      )}
      </div>
    </>
  );
};
